/**
 * Free Image API Service
 * Provides functions to fetch high-quality, royalty-free images from multiple sources
 */

// Unsplash & Pexels Proxy URL
const PROXY_API_URL = '/.netlify/functions/image-proxy';

/**
 * Fetch images from Unsplash via Proxy
 * @param {string} query - Search query
 * @param {number} count - Number of images to fetch (default: 3)
 * @returns {Promise<Array>} Array of image objects with url and attribution
 */
export const fetchFromUnsplash = async (query, count = 3) => {
  try {
    const params = new URLSearchParams({
      query,
      count,
      source: 'unsplash'
    });

    const response = await fetch(`${PROXY_API_URL}?${params}`);
    if (!response.ok) throw new Error('Unsplash Proxy failed');

    return await response.json();
  } catch (error) {
    console.warn('Unsplash fetch failed:', error);
    return [];
  }
};

/**
 * Fetch images from Pexels via Proxy
 * @param {string} query - Search query
 * @param {number} count - Number of images to fetch (default: 3)
 * @returns {Promise<Array>} Array of image objects
 */
export const fetchFromPexels = async (query, count = 3) => {
  try {
    const params = new URLSearchParams({
      query,
      count,
      source: 'pexels'
    });

    const response = await fetch(`${PROXY_API_URL}?${params}`);
    if (!response.ok) throw new Error('Pexels Proxy failed');

    return await response.json();
  } catch (error) {
    console.warn('Pexels fetch failed:', error);
    return [];
  }
};

/**
 * Fetch images from Pixabay
 * @param {string} query - Search query
 * @param {number} count - Number of images to fetch (default: 3)
 * @returns {Promise<Array>} Array of image objects
 */
export const fetchFromPixabay = async (query, count = 3) => {
  // Pixabay support temporarily disabled or moved to proxy in future
  // For now return empty to fallback to other sources
  return [];
};

/**
 * Get relevant images for article based on keywords
 * Tries multiple sources, returns first successful result
 * @param {string} topic - Article topic/keyword
 * @param {number} count - Number of images needed
 * @returns {Promise<Array>} Array of image objects
 */
export const getArticleImages = async (topic, count = 3) => {
  // Try Unsplash first (best quality)
  const unsplashImages = await fetchFromUnsplash(topic, count);
  if (unsplashImages.length > 0) return unsplashImages;

  // Fallback to Pexels
  const pexelsImages = await fetchFromPexels(topic, count);
  if (pexelsImages.length > 0) return pexelsImages;

  // No images found
  return [];
};

/**
 * Map article topics to relevant search queries
 * Helps find better images by using specific keywords
 */
const topicKeywordMap = {
  'iptv': ['streaming', 'live tv', 'television', 'cord cutting'],
  'device': ['smart tv', 'firestick', 'roku', 'android tv', 'device setup'],
  'setup': ['installation', 'configuration', 'tutorial', 'guide'],
  'region': ['geographic', 'location', 'travel', 'map'],
  'comparison': ['comparison', 'versus', 'contrast', 'choice'],
  'streaming': ['video streaming', 'online video', 'streaming service'],
  'sports': ['sports', 'games', 'broadcast', 'live sports'],
  'legal': ['law', 'legal', 'document', 'compliance'],
  'security': ['security', 'privacy', 'lock', 'protection']
};

/**
 * Get optimized search query for article topic
 * @param {string} articleTitle - Article title
 * @returns {string} Optimized search query
 */
export const getOptimizedSearchQuery = (articleTitle) => {
  const titleLower = articleTitle.toLowerCase();

  // Find matching topic and return relevant keywords
  for (const [topic, keywords] of Object.entries(topicKeywordMap)) {
    if (titleLower.includes(topic)) {
      return keywords[Math.floor(Math.random() * keywords.length)];
    }
  }

  // Default: use first 3-4 words from title
  return titleLower.split(' ').slice(0, 4).join(' ');
};

/**
 * Fallback placeholder image (if no API images available)
 * Returns a solid color placeholder
 */
export const getPlaceholderImage = (topic = 'iptv') => {
  const colors = {
    'iptv': 'from-primary to-primary/70',
    'device': 'from-purple-600 to-purple-800',
    'setup': 'from-blue-600 to-blue-800',
    'region': 'from-orange-600 to-orange-800',
    'comparison': 'from-pink-600 to-pink-800',
    'streaming': 'from-green-600 to-green-800',
    'sports': 'from-red-600 to-red-800',
    'legal': 'from-gray-600 to-gray-800',
    'security': 'from-yellow-600 to-yellow-800'
  };

  const topicLower = topic.toLowerCase();
  const colorClass = colors[Object.keys(colors).find(key => topicLower.includes(key)) || 'iptv'];

  return {
    url: null,
    placeholder: true,
    color: colorClass,
    alt: `${topic} related image`
  };
};
